import './toast.css';
import { renderIcon, type IconName } from '../../icons';

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

export interface ToastOptions {
  duration?: number;
  description?: string;
  action?: { label: string; onClick: () => void };
}

export interface ToastApi {
  success: (message: string, opts?: ToastOptions) => string;
  error: (message: string, opts?: ToastOptions) => string;
  warning: (message: string, opts?: ToastOptions) => string;
  info: (message: string, opts?: ToastOptions) => string;
  dismiss: (id: string) => void;
  dismissAll: () => void;
}

interface ToastEntry {
  id: string;
  el: HTMLElement;
  variant: ToastVariant;
  duration: number;
  timer: ReturnType<typeof setTimeout> | null;
  startedAt: number;
  remaining: number;
  progressEl: HTMLElement | null;
  removing: boolean;
}

const VARIANT_ICON: Record<ToastVariant, IconName> = {
  success: 'success-circle',
  error: 'alert-triangle',
  warning: 'alert-triangle',
  info: 'info',
};

const DEFAULT_DURATION = 4500;
const SLIDE_OUT_MS = 200;

function makeId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function createFactory(): ToastApi {
  let container: HTMLElement | null = null;
  const entries = new Map<string, ToastEntry>();

  const ensureContainer = (): HTMLElement => {
    if (container && container.isConnected) return container;
    const el = document.createElement('div');
    el.className = 'tkos-toast-container';
    el.setAttribute('aria-live', 'polite');
    document.body.appendChild(el);
    container = el;
    return el;
  };

  const removeNow = (entry: ToastEntry): void => {
    if (entry.timer !== null) {
      clearTimeout(entry.timer);
      entry.timer = null;
    }
    entry.el.remove();
    entries.delete(entry.id);
    if (container && entries.size === 0 && container.isConnected) {
      container.remove();
      container = null;
    }
  };

  const beginRemoval = (entry: ToastEntry): void => {
    if (entry.removing) return;
    entry.removing = true;
    if (entry.timer !== null) {
      clearTimeout(entry.timer);
      entry.timer = null;
    }
    entry.el.classList.add('tkos-toast--leaving');
    const fallback = window.setTimeout(() => removeNow(entry), SLIDE_OUT_MS + 50);
    const onEnd = (ev: AnimationEvent): void => {
      if (ev.target !== entry.el) return;
      window.clearTimeout(fallback);
      entry.el.removeEventListener('animationend', onEnd);
      removeNow(entry);
    };
    entry.el.addEventListener('animationend', onEnd);
  };

  const startTimer = (entry: ToastEntry): void => {
    if (entry.duration <= 0 || entry.removing) return;
    entry.startedAt = Date.now();
    entry.timer = setTimeout(() => {
      entry.timer = null;
      beginRemoval(entry);
    }, entry.remaining);
  };

  const pauseTimer = (entry: ToastEntry): void => {
    if (entry.duration <= 0 || entry.removing) return;
    if (entry.timer !== null) {
      clearTimeout(entry.timer);
      entry.timer = null;
      const elapsed = Date.now() - entry.startedAt;
      entry.remaining = Math.max(0, entry.remaining - elapsed);
    }
    if (entry.progressEl) {
      const cs = getComputedStyle(entry.progressEl);
      const transform = cs.transform;
      entry.progressEl.style.animation = 'none';
      entry.progressEl.style.transform = transform === 'none' ? 'scaleX(1)' : transform;
    }
  };

  const resumeTimer = (entry: ToastEntry): void => {
    if (entry.duration <= 0 || entry.removing) return;
    if (entry.progressEl && entry.remaining > 0) {
      const ratio = entry.remaining / entry.duration;
      entry.progressEl.style.transform = `scaleX(${ratio})`;
      // Force reflow so the new animation picks up the current transform.
      void entry.progressEl.offsetWidth;
      entry.progressEl.style.animation = `tkos-progress ${entry.remaining}ms linear forwards`;
    }
    startTimer(entry);
  };

  const buildToast = (
    id: string,
    variant: ToastVariant,
    message: string,
    opts: ToastOptions,
  ): ToastEntry => {
    const duration = opts.duration ?? DEFAULT_DURATION;

    const el = document.createElement('div');
    el.className = `tkos-toast tkos-toast--${variant}`;
    el.dataset['variant'] = variant;
    el.setAttribute('role', variant === 'error' || variant === 'warning' ? 'alert' : 'status');
    el.setAttribute(
      'aria-live',
      variant === 'error' ? 'assertive' : 'polite',
    );
    el.setAttribute('aria-atomic', 'true');

    const strip = document.createElement('span');
    strip.className = 'tkos-toast__strip';
    strip.setAttribute('aria-hidden', 'true');
    el.appendChild(strip);

    const iconWrap = document.createElement('span');
    iconWrap.className = 'tkos-toast__icon';
    iconWrap.appendChild(renderIcon(VARIANT_ICON[variant], { size: 'md' }));
    el.appendChild(iconWrap);

    const body = document.createElement('div');
    body.className = 'tkos-toast__body';

    const msg = document.createElement('div');
    msg.className = 'tkos-toast__message';
    msg.textContent = message;
    body.appendChild(msg);

    if (opts.description) {
      const desc = document.createElement('div');
      desc.className = 'tkos-toast__description';
      desc.textContent = opts.description;
      body.appendChild(desc);
    }

    el.appendChild(body);

    if (opts.action) {
      const action = opts.action;
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'tkos-toast__action';
      btn.textContent = action.label;
      btn.addEventListener('click', () => {
        try {
          action.onClick();
        } finally {
          beginRemoval(entry);
        }
      });
      el.appendChild(btn);
    }

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'tkos-toast__close';
    close.setAttribute('aria-label', 'Dismiss notification');
    close.appendChild(renderIcon('close', { size: 'sm' }));
    close.addEventListener('click', () => beginRemoval(entry));
    el.appendChild(close);

    let progressEl: HTMLElement | null = null;
    if (duration > 0) {
      progressEl = document.createElement('span');
      progressEl.className = 'tkos-toast__progress';
      progressEl.setAttribute('aria-hidden', 'true');
      progressEl.style.animation = `tkos-progress ${duration}ms linear forwards`;
      el.appendChild(progressEl);
    }

    const entry: ToastEntry = {
      id,
      el,
      variant,
      duration,
      timer: null,
      startedAt: Date.now(),
      remaining: duration,
      progressEl,
      removing: false,
    };

    el.addEventListener('mouseenter', () => pauseTimer(entry));
    el.addEventListener('mouseleave', () => resumeTimer(entry));
    el.addEventListener('focusin', () => pauseTimer(entry));
    el.addEventListener('focusout', () => resumeTimer(entry));

    return entry;
  };

  const show = (
    variant: ToastVariant,
    message: string,
    opts: ToastOptions = {},
  ): string => {
    const root = ensureContainer();
    const id = makeId();
    const entry = buildToast(id, variant, message, opts);
    entries.set(id, entry);
    root.appendChild(entry.el);
    startTimer(entry);
    return id;
  };

  return {
    success: (message, opts) => show('success', message, opts),
    error: (message, opts) => show('error', message, opts),
    warning: (message, opts) => show('warning', message, opts),
    info: (message, opts) => show('info', message, opts),
    dismiss: (id) => {
      const entry = entries.get(id);
      if (entry) beginRemoval(entry);
    },
    dismissAll: () => {
      for (const entry of Array.from(entries.values())) {
        beginRemoval(entry);
      }
    },
  };
}

export const toast: ToastApi = createFactory();
